import React, {
  useState,
  useEffect,
  FC,
  createContext,
  useContext,
} from "react";
import Content from "../../layout/content/Content";
import { Card } from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
} from "../../components/Component";
import { Steps, Step } from "react-step-builder";
import { useForm, FormProvider } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import WorkBoqGeneralDetails from "./workBoqCreation/workBoqGeneralDetails";
import WorkBoqOrderLines from "./workBoqCreation/workBoqOrderLines";
import WorkBoqPreview from "./workBoqCreation/workBoqPreview";
import Header_WorkBoq from "./workBoqCreation/Header_WorkBoq";
import WorkBoqAside from "./workBoqAside";
import { Link } from "react-router-dom";
import workBoqApis from "../../api/WorkOrderManagement/WorkBOQ";
import { message, Spin, Flex } from "antd";

// Create context for edit mode
export const EditModeContext = createContext({
  isEditMode: false,
  editWorkBoqId: null as string | null,
  originalData: null as any,
});

const config = {
  before: Header_WorkBoq as React.ComponentType<{}> as () => React.ReactElement,
};

const WorkBoqCreationMain: FC = () => {
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editWorkBoqId, setEditWorkBoqId] = useState<string | null>(null);
  const [originalData, setOriginalData] = useState<any>(null);

  const formMethods = useForm({
    mode: "onChange",
    defaultValues: {
      boqTitle: "",
      boqDescription: "",
      siteId: "",
      siteLabel: "",
      projectId: "",
      projectLabel: "",
      contractorId: "",
      contractorLabel: "",
      budgetId: "",
      budgetLabel: "",
      completionAlertsFrom: "",
      materialBoqId: "",
      materialBoqLabel: "",
      workLevel: "",
      workOrderLines: [],
      approvalRequired: "false",
      approvalsData: "",
    },
  });

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const conversionData = location.state?.data;

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  // Fetch work BOQ data for edit mode
  const fetchWorkBoqData = async (workBoqId: string) => {
    try {
      setIsLoading(true);
      const response: any = await workBoqApis.getWorkBoqById(workBoqId);

      if (
        response &&
        response.status &&
        response.data &&
        response.data.workBoq
      ) {
        const apiData = response.data.workBoq;

        // Determine work level for proper field mapping
        const workLevel =
          apiData.segmentLevel &&
          (apiData.segmentLevel === "Block" || apiData.segmentLevel === "Tower")
            ? "Tower / Block"
            : apiData.segmentLevel || "";

        // Map work order lines for form format
        const mappedWorkOrderLines =
          apiData.workBoqLines?.map((line: any) => {
            // For Villa work level, the villa is stored in siteStructureUnitId (not subUnitId)
            const isVilla = workLevel === "Villa";

            return {
              id: line.workBoqLineId?.toString() || "",
              workCategory: line.workCategoryId?.toString() || "",
              workCategoryName: line.workCategoryName || "",
              taskTitle: line.taskTitle || "",
              taskDescription: line.taskDescription || "",
              quantity: line.quantity || 0,
              uom: line.uomId?.toString() || "",
              rate: line.rate || 0,
              uomName: line.uomName || "",
              // Map new location fields from API response
              towerVilla: line.siteStructureUnitId?.toString() || "",
              towerVillaName: line.siteStructureUnitName || "",
              floorName: line.floorNo || "",
              // For Villa: flatName stores the villa ID (from siteStructureUnitId)
              // For others: flatName stores the flat ID (from siteStructureSubUnitId)
              flatName: isVilla
                ? line.siteStructureUnitId?.toString() || ""
                : line.siteStructureSubUnitId?.toString() || "",
              flatNameDisplay: isVilla
                ? line.siteStructureUnitName || ""
                : line.siteStructureSubUnitName || "",
              locationId: line.locationId?.toString() || "",
              // Store new fields for API submission
              siteStructureUnitId: line.siteStructureUnitId || null,
              floorNo: line.floorNo || null,
              siteStructureSubUnitId: line.siteStructureSubUnitId || null,
              budgetLineId: line.budgetLineId?.toString() || "",
              budgetLineName: line.budgetLineName || "",
              allocatedAmount: line.budgetAllocation || 0,
              allocatedMaterials:
                line.workBoqLineMaterials?.map((material: any) => ({
                  materialId: material.materialId?.toString() || "",
                  materialName: material.materialName || "Unknown Material",
                  quantity: material.allocatedQty || 0,
                  categoryId: material.categoryId?.toString() || "",
                  category: material.categoryName || "",
                  uomId: line.uomId?.toString() || "",
                  uom: line.uomName || "",
                })) || [],
              uploadedDocs: line.dms || [],
            };
          }) || [];

        // Set form data
        formMethods.reset({
          boqTitle: apiData.boqTitle || "",
          boqDescription: apiData.boqDescription || "",
          siteId: apiData.siteId?.toString() || "",
          siteLabel: apiData.siteName || "",
          projectId: apiData.projectId?.toString() || "",
          projectLabel: apiData.projectTitle || "",
          contractorId: apiData.contractorId?.toString() || "",
          contractorLabel: apiData.contractorName || "",
          budgetId: apiData.budgetHeaderId?.toString() || "",
          budgetLabel: apiData.budgetName || "",
          completionAlertsFrom: apiData.completionAlertsFrom?.toString() || "",
          materialBoqId: apiData.materialBoqId?.toString() || "", // Not provided in response
          materialBoqLabel: apiData.materialBoqName || "",
          workLevel:
            (apiData.segmentLevel && apiData.segmentLevel === "Block") ||
            (apiData.segmentLevel && apiData.segmentLevel === "Tower")
              ? "Tower / Block"
              : apiData.segmentLevel || "",
          workOrderLines: mappedWorkOrderLines,
          approvalRequired: apiData.approvalRequired?.toString() || "false",
          approvalsData: JSON.stringify({
            data: (apiData.workBoqApprovals || []).map(
              (approval: any, index: number) => ({
                roleId: approval.roleId || null,
                level: approval.level || `Level_${index + 1}`, // Create level based on index
                userId: approval.userId?.toString() || "",
                approvalStage: approval.approvalStage || "",
                roleName: approval.roleName || "",
                userName: approval.userName || "",
                // Keep original data for debugging
                originalData: approval,
              })
            ),
          }),
        });

        // Store original data for change detection
        setOriginalData({
          boqTitle: apiData.boqTitle || "",
          boqDescription: apiData.boqDescription || "",
          siteId: apiData.siteId?.toString() || "",
          projectId: apiData.projectId?.toString() || "",
          contractorId: apiData.contractorId?.toString() || "",
          budgetId: apiData.budgetHeaderId?.toString() || "",
          completionAlertsFrom: apiData.completionAlertsFrom?.toString() || "",
          materialBoqId: apiData.materialBoqId?.toString() || "",
          workLevel:
            (apiData.segmentLevel && apiData.segmentLevel === "Block") ||
            (apiData.segmentLevel && apiData.segmentLevel === "Tower")
              ? "Tower / Block"
              : apiData.segmentLevel || "",
          approvalRequired: apiData.approvalRequired?.toString() || "false",
          approvalsData: JSON.stringify({
            data: (apiData.workBoqApprovals || []).map(
              (approval: any, index: number) => ({
                roleId: approval.roleId || null,
                level: approval.level || `Level_${index + 1}`,
                userId: approval.userId?.toString() || "",
                approvalStage: approval.approvalStage || "",
                roleName: approval.roleName || "",
                userName: approval.userName || "",
              })
            ),
          }),
          workOrderLines: mappedWorkOrderLines,
        });

        message.success("Work BOQ data loaded successfully");
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work BOQ details"
        );
      }
    } catch (error: any) {
      console.error("Error fetching Work BOQ details:", error);
      message.error("Failed to load Work BOQ data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Check for edit mode from URL params
    const editId = searchParams.get("edit");
    if (editId) {
      setIsEditMode(true);
      setEditWorkBoqId(editId);
      fetchWorkBoqData(editId);
    }
  }, [searchParams]);

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  if (isLoading) {
    return (
      <>
        <Head title={isEditMode ? "Edit Work BOQ" : "Work BOQ Creation"} />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  return (
    <>
      <Head title={isEditMode ? "Edit Work BOQ" : "Work BOQ Creation"} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            {/* <div
              className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
                sm ? "content-active" : ""
              }`}
            >
              <WorkBoqAside updateSm={setSm} sm={sm} />
            </div> */}
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      {isEditMode ? "Edit BOQ" : "Create New BOQ"}
                    </BlockTitle>
                  </BlockHeadContent>
                  <BlockHeadContent>
                    <Link
                      to={(() => {
                        const searchParams = new URLSearchParams(
                          location.search
                        );
                        const flowType = searchParams.get("flowType");
                        const flowTypeQuery = flowType
                          ? `?flowType=${flowType}`
                          : "";
                        return `/work-boq-list${flowTypeQuery}`;
                      })()}
                    >
                      <Button
                        outline
                        className="bg-white d-none d-sm-inline-flex"
                      >
                        <Icon name="arrow-left"></Icon>
                        <span>Back to List</span>
                      </Button>
                      <Button
                        outline
                        className="btn-icon bg-white d-inline-flex d-sm-none"
                      >
                        <Icon name="arrow-left"></Icon>
                      </Button>
                    </Link>
                  </BlockHeadContent>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Block className="mt-1">
                <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix">
                  <FormProvider {...formMethods}>
                    <EditModeContext.Provider
                      value={{ isEditMode, editWorkBoqId, originalData }}
                    >
                      <Steps config={config}>
                        <Step component={WorkBoqGeneralDetails} />
                        <Step component={WorkBoqOrderLines} />
                        <Step component={WorkBoqPreview} />
                      </Steps>
                    </EditModeContext.Provider>
                  </FormProvider>
                </div>
              </Block>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default WorkBoqCreationMain;
